import React, { ReactElement, useEffect, useState } from 'react';
import WidgetActionsMenu from 'adapter/in/dashboards/pages/Widget/ActionsMenu';
import { WidgetType } from 'domain/entity/widget';
import { dispatchTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import ConfirmActionModal from 'adapter/in/common/components/ConfirmActionModal/ConfirmActionModal';
import i18n from 'asset/translation';
import gtm from 'adapter/in/reports/config/gtm';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { isSome } from 'fp-ts/lib/Option';
import { DeleteTextBlockCriteriaBuilder } from 'use-case/widgets/delete/deleteTextBlockCriteria';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';

type TextBlockActionsMenuProps = {
    widgetId: string;
    dashboardId: string;
    showMenu: boolean;
};

export const TextBlockActionsMenuContainer = (props: TextBlockActionsMenuProps): ReactElement => {
    const { widgetId, dashboardId, showMenu } = props;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { deleteTextBlock } = useTextBlock();
    const { execute: remove, error, isLoading, data } = useCase(deleteTextBlock, false);
    const { setEditorBeingEdited } = dispatchTextBlockData();
    const { getLayouts } = dispatchReportLayoutsData();
    const [isRemoving, setIsRemoving] = useState(false);

    useEffect(() => {
        if (!isLoading && isRemoving && dashboardId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(dashboardId).build());
        }
    }, [isLoading, isRemoving, dashboardId]);

    useEffect(() => {
        if (isSome(data)) {
            setIsRemoving(false);
        }
    }, [data]);

    const handleDelete = () => {
        remove(DeleteTextBlockCriteriaBuilder.create().withWidgetId(widgetId).withDashboardId(dashboardId).build());
        setIsRemoving(true);
        setShowDeleteModal(false);
    };
    const openDeleteModal = () => {
        setShowDeleteModal(true);
    };

    const onDeleteModal = (
        <ConfirmActionModal
            isOpen={showDeleteModal}
            onClose={() => {
                setShowDeleteModal(false);
            }}
            mainButton={() => {
                handleDelete();
            }}
            hasError={isSome(error)}
            isLoading={isLoading}
            message={i18n.t('widgets.delete.warningMsg')}
            title={i18n.t('widgets.actionDelete')}
            mainButtonText={i18n.t('widgets.delete.yes')}
            mainButtonVariant='destructive'
            dataIdContext={{
                main: gtm.deleteModal.delete,
                cancel: gtm.deleteModal.cancel,
                tryAgain: gtm.deleteModal.tryAgain,
            }}
        />
    );

    return (
        <>
            <WidgetActionsMenu
                show={showMenu}
                showOptions={false}
                onDelete={() => {
                    openDeleteModal();
                }}
                onEditTextBlock={() => {
                    setEditorBeingEdited(widgetId);
                }}
                isEditDisabled={() => false}
                widgetType={WidgetType.RICH_TEXT}
            />
            {onDeleteModal}
        </>
    );
};

export default TextBlockActionsMenuContainer;
